// 경제 데이터: 강화 재료·강화 비용·강화 능력치·골드 수입원·상점 가격(정본: docs/CONTENT-90D-PRD.md 4.3·4.4·4.6·5, D0a 시뮬 결정).
// 데이터와 순수 계산만 둔다(three import 금지). 게임 연결은 c009. 수치 근거와 대안은 docs/loop/content-90d/sim-report.md 경제 절.
// 설계 기준(로스트아크·던파식): 재료 등급은 "장비 단계(아이템 레벨)"를 따르고, 수량은 강화 단계를 따른다.
// 지역이 바뀌면 낮은 등급 강화석은 상위 등급으로 바꿔 쓰고(5:1 + 골드), 강화 단계는 계승(무료, −1)으로 새 장비에 옮긴다.
import type { Grade, RegionNo } from './types';

// ── 강화석 등급(PRD 4.4) ─────────────────────────────
/** 0 하 · 1 중 · 2 상 · 3 최상 */
export type StoneTier = 0 | 1 | 2 | 3;
export const STONE_TIERS: readonly { readonly tier: StoneTier; readonly id: string; readonly name: string; readonly minIlvl: number }[] = [
  // 지역1-2 장비(아이템 레벨 1~29)
  { tier: 0, id: 'stone_low', name: '강화석(하)', minIlvl: 1 },
  // 지역3-4(30~54)
  { tier: 1, id: 'stone_mid', name: '강화석(중)', minIlvl: 30 },
  // 지역5-6·하드(55~79)
  { tier: 2, id: 'stone_high', name: '강화석(상)', minIlvl: 55 },
  // 헬·레이드4(80~)
  { tier: 3, id: 'stone_top', name: '강화석(최상)', minIlvl: 80 },
];
/** 아이템 레벨 → 그 장비 강화에 드는 강화석 등급. 같은 등급 강화석이 그 아이템 레벨의 콘텐츠에서 떨어진다 */
export function stoneTierForIlvl(ilvl: number): StoneTier {
  let t: StoneTier = 0;
  for (const s of STONE_TIERS) if (ilvl >= s.minIlvl) t = s.tier;
  return t;
}

/**
 * 콘텐츠 아이템 레벨 = 지역 장비 레벨(그 지역 권장 구간 시작) + 난이도 가산(하드 +10, 헬 +20).
 * 하드·헬 드롭이 같은 지역 노말보다 한 단계 위 장비가 되게 한다(파밍 동기).
 */
export const DIFFICULTY_ILVL_ADD = { normal: 0, hard: 10, hell: 20 } as const;

// ── 강화 비용(한 번 시도) ─────────────────────────────
/**
 * 강화 시도 1회 강화석 수(인덱스 = 올라갈 단계 1..15, 0번은 0). 등급은 장비 아이템 레벨의 강화석.
 * 하락 구간(+13~15)은 시도 수가 많아(천장 포함 단계당 3.6~5.9회) 1회 수량은 +12와 같게 두고, 하루 공급이 큰 헬에서 채운다.
 * 결과(D0a 시뮬, PR #153 리뷰 반영): 6부위 +9 ≈ 11일차, +12 ≈ 42일차(상한 해금 19일차), +15 ≈ 95일차(해금 62일차).
 */
export const ENHANCE_STONES: readonly number[] = [0, 2, 2, 3, 3, 4, 5, 6, 7, 8, 16, 18, 20, 20, 20, 20];
/**
 * 강화 시도 1회 골드 = 그 시도의 강화석 수 × 강화석당 골드(아이템 레벨 1·일반 등급) × 아이템 레벨 배율 × 등급 배율.
 * 골드를 강화석 수에 비례시켜 두 재료가 같은 속도로 묶이게 한다(단계별 골드 표를 따로 두면 +13~15에서 골드만 20배로 튀어 한쪽만 병목이 됐다, D0a 시뮬).
 */
export const ENHANCE_GOLD_PER_STONE = 130;
export const ENHANCE_GOLD_ILVL_GROWTH = 1.049;
/** 등급별 강화 비용 배율(강화석·골드 모두). 좋은 장비일수록 강화가 비싸다(던파식) */
export const ENHANCE_GRADE_COST_MULT: Readonly<Record<Grade, number>> = { common: 1, rare: 1.1, epic: 1.25, set: 1.4, legend: 1.6 };

export interface EnhanceTryCost { readonly tier: StoneTier; readonly stones: number; readonly gold: number }
/** +to로 올리는 시도 1회 비용(장비 아이템 레벨·등급 기준) */
export function enhanceTryCost(to: number, ilvl: number, grade: Grade): EnhanceTryCost {
  const tier = stoneTierForIlvl(ilvl);
  const g = ENHANCE_GRADE_COST_MULT[grade];
  return { tier, stones: Math.ceil(ENHANCE_STONES[to] * g), gold: Math.round(ENHANCE_STONES[to] * ENHANCE_GOLD_PER_STONE * Math.pow(ENHANCE_GOLD_ILVL_GROWTH, ilvl - 1) * g) };
}

/**
 * 강화 능력치: +e 장비의 기본 능력치(등급 배율 뒤) 배율. 인덱스 = 강화 단계 0..15.
 * +1~5 단계당 +5%, +6~9 +8%, +10~12 +12%, +13~15 +20% (누적 합). +15 = ×2.53.
 * 하락 구간 3단계가 +0→+12 전체(+93%)에 맞먹는 +60%를 줘서 엔드게임 목표가 분명하다.
 */
export const ENHANCE_STAT_BONUS: readonly number[] = (() => {
  const out = [1];
  for (let e = 1; e <= 15; e++) out.push(Math.round((out[e - 1] + (e <= 5 ? 0.05 : e <= 9 ? 0.08 : e <= 12 ? 0.12 : 0.2)) * 1000) / 1000);
  return out;
})();

/**
 * 보호 주문서 지급 시작: +13이 열리는 지역6 클리어 뒤부터(PRD 4.3 주 약 10장). 그 전 주간 의뢰·레이드·탑의 보호 주문서 칸은
 * 그 날 장비 등급 강화석 preUnlockStones개로 준다. 해금 전에 쌓이면 해금 순간 약 69장이 한꺼번에 풀려 +15가 주문서 제약 없이
 * 골드·강화석만으로 끝났다(D0a 시뮬) — PRD의 "6부위 약 3.2주" 전제가 깨진다.
 * 칸당 5 → 7(PR #153 리뷰 재보정): 레이드 주문서는 레이드별 주 첫 클리어라 해금 전 레이드 칸이 3 → 1~2개로 줄어(서로 다른 레이드 수)
 * 4~5주 강화가 강화석에 막혀 지출/수입이 56%로 떨어졌다. 7이면 65%(6이면 4~5주 주당 지출이 3주보다 작다).
 */
export const PROTECT_SUPPLY = { fromRegionClear: 6 as RegionNo, preUnlockStones: 7 } as const;

/** 강화석 상위 변환(대장간): 아래 등급 ratio개 → 위 등급 1개 + 골드(gold[위 등급]). 지난 지역 재료가 쓰레기가 되지 않게 한다 */
export const STONE_CONVERT = { ratio: 5, gold: [0, 200, 600, 1500] as readonly number[] } as const;

/**
 * 강화석 아래 등급 변환 계획(5:1, 한 등급씩). `need`(소수 가능)개의 tier 등급 강화석을 모자란 만큼 만든다.
 * 위 등급부터 아래로 내려가며, 한 등급을 만들 때 먼저 그 아래 등급 보유분(정수 개)을 쓰고 모자란 몫만 다시 그 아래에서 변환한다 —
 * 중간 등급 결과가 그 등급 보유분과 합쳐지므로 하 5 + 중 4 → 상 1이 된다(c009 Codex P2). 단계마다 그 등급 변환 수수료(STONE_CONVERT.gold[만든 등급])를 낸다.
 * take[k] = 소모하는 k 등급 보유 강화석 수, made = 만들어지는 tier 등급 수(ceil(need)), ok = 맨 아래 등급까지 모자람 없이 충당됨.
 */
export function planStoneConversion(stones: readonly number[], tier: number, need: number): { ok: boolean; fee: number; take: number[]; made: number } {
  const take = stones.map(() => 0);
  if (!(need > 0)) return { ok: true, fee: 0, take, made: 0 };
  const ratio = STONE_CONVERT.ratio;
  const made = Math.ceil(need - 1e-9);
  let want = made, fee = 0;
  for (let k = tier; k >= 1 && want > 0; k--) {
    fee += want * STONE_CONVERT.gold[k];
    const lower = want * ratio;
    const used = Math.min(Math.floor(stones[k - 1] + 1e-9), lower);
    take[k - 1] = used;
    want = lower - used;
  }
  return { ok: want <= 0, fee, take, made };
}

// ── 분해·판매 ──────────────────────────────────────
/** 분해 강화석(그 장비 아이템 레벨 등급). 일반·희귀는 분해 대신 판매가 기본(SELL_GOLD) */
export const DISMANTLE_STONES: Readonly<Record<Grade, number>> = { common: 0, rare: 0, epic: 1, set: 2, legend: 3 };
/** 상점 판매 골드(지역1 기준, 실제 = × 지역 골드 배율) */
export const SELL_GOLD: Readonly<Record<Grade, number>> = { common: 12, rare: 30, epic: 80, set: 0, legend: 0 };

// ── 골드 수입원 ─────────────────────────────────────
/**
 * 지역별 던전 골드(노말). perKill: 처치당(× 스테이지 배율), clear: 스테이지 클리어 기본, card: 카드 3장 중 1 평균.
 * 지역1·2는 기존 정본(regions.ts R1 200+3/처치·카드 100~300, R2 500+6/처치·카드 200~400)과 같다.
 * 지역3부터 처치 골드 ×1.85/지역(XP 2.2보다 느리게: 골드 인플레이션 억제, 강화 골드의 아이템 레벨 성장과 같은 결).
 */
export const REGION_GOLD: readonly { readonly perKill: number; readonly clear: number; readonly card: number }[] = [
  { perKill: 3, clear: 200, card: 200 },
  { perKill: 6, clear: 500, card: 300 },
  { perKill: 11, clear: 900, card: 550 },
  { perKill: 20, clear: 1700, card: 1000 },
  { perKill: 37, clear: 3100, card: 1850 },
  { perKill: 68, clear: 5700, card: 3400 },
];
/** 지역 골드 배율(지역1 처치 골드 대비). 의뢰·일일 던전·판매·상점 가격이 함께 쓴다 */
export function regionGoldMult(region: RegionNo): number {
  return REGION_GOLD[region - 1].perKill / REGION_GOLD[0].perKill;
}
/**
 * 보스 처치 추가 골드(그 지역 clear 대비 배율). D0a 시뮬이 가정했지만 게임은 주지 않았다. S3(스테이지 보상 차등)부터 게임은
 * special.ts CLEAR_REWARD_MULT(클리어 골드·카드 ×0.95/2/3.5)를 쓰고, 시뮬도 그 모델이 기본이다(이 상수는 'S3 이전 시뮬' 비교용으로만 남는다).
 */
export const BOSS_GOLD_MULT = { mid: 1.5, final: 3 } as const;
/**
 * 난이도 보상 배율: 골드·강화석 드롭. 골드는 노말 10스테이지(×2.08)에서 하드 1스테이지로 넘어갈 때 수입이 줄지 않고,
 * 한 단계 위 아이템 레벨의 강화 골드 성장(1.049^10 ≈ 1.6)을 따라잡게 둔다. 강화석은 헬이 엔드게임 재료 파밍터(PRD 7절)라 가장 크다.
 * 헬 골드 4.5 → 4.8·강화석 3.5 → 4.2(PR #153 리뷰 재보정): 주간 레이드를 서로 다른 레이드 3회로 바로잡자 헬 레이드 수입이
 * 최상 강화석 135 → 45개/주, 골드 450k → 240k/주로 줄어 6부위 +15가 98일(Lv99 88일 +10)로 밀리고 전설 재료 묶임이 1.66배였다.
 * 4.2·4.8이면 +15 94일, 전설 +11→+15 강화석 31일·골드 30일·주문서 21일(1.47배). 강화석만 올리면(4.2·골드 4.5) 12~13주 골드 적자.
 * c009 Codex P2 재보정(보스 드롭표를 스테이지 진행도로 연 뒤): 보스 표의 높은 강화석 확률(0.025·0.04)이 지역 진입 초반에 빠져 처치 강화석이
 * 줄었다 → 노말 1 → 1.6(4~5주 지출/수입 54% → 60% 이상), 헬 4.2 → 4.8(+15 100일 → 95일, Lv99 88일 ±7). 하드는 그대로 2.
 * S3 재보정(첫 클리어 보상 도입): 노말 1.6 → 1.3. 첫 클리어 강화석(스테이지·난이도별 1회: 일반 2·n-5 5·n-10 10, 지역당 31개)이 총공급에
 * 더해지므로 처치 강화석을 덜어 경력 총공급을 맞췄다(100일 누적 강화석 7,550 → 7,527개, −0.3%; 노말 밴드만 보면 3,683 → 3,594개, −2.4%).
 * 1.6 그대로면 강화 도달일(+12가 상한 해금 +14일보다 하루 빨라짐)·8~9주 순수입(−50만)·밴드별 타수 단조 불변식이 깨진다.
 */
export const DIFFICULTY_LOOT = {
  normal: { gold: 1, stones: 1.3 },
  hard: { gold: 3, stones: 2 },
  hell: { gold: 4.8, stones: 4.8 },
} as const;

/**
 * 거래 금고 출석 골드(docs/MARKET-GOLD-AUTHORITY.md 6절, 지휘 결정 2026-10-28).
 * 하루 base 골드, streakDays일 연속 출석한 날만 streakBonus를 더 준다(그날 base+bonus). 레벨·환생과 무관한 고정값(골드 배율 gm을 곱하지 않는다).
 * 금고 골드는 우편으로 출금해 가방 골드가 되므로 플레이어 골드 수입으로 센다. 거래소는 아직 꺼져 있지만(MARKET_NET false) 경제 시뮬은 켜진 상태를 가정한다.
 */
export const MARKET_DAILY_GOLD = { base: 300, streakDays: 7, streakBonus: 700 } as const;
/** 출석 금고 골드 하루 기대값: base + streakBonus / streakDays (= 400G) */
export function marketDailyGoldExpected(): number {
  return MARKET_DAILY_GOLD.base + MARKET_DAILY_GOLD.streakBonus / MARKET_DAILY_GOLD.streakDays;
}

/** 일일 던전 보상(지역1 기준 값, 실제 골드 = × 그 날 사냥 지역 골드 배율, 강화석은 그 날 장비 등급). 의뢰 보상은 quests.ts */
export const ROUTINE = {
  /** 일일: 황금 보물고(마을② 해금, 1일 2회) 회당 골드 */
  goldVault: { perDay: 2, gold: 1500, town: 2 },
  /** 일일: 강화석 광산(마을② 해금, 1일 2회) 회당 강화석 */
  stoneMine: { perDay: 2, stones: 6, town: 2 },
} as const;

// ── 상점·소모 ───────────────────────────────────────
/** 상점 단계(마을 1..6)별 가격 배율: 비약 제작비·주문서·재련·소켓 비용에 곱한다. 지역 골드 배율과 같은 결 */
export const SHOP_TIER_PRICE_MULT: readonly number[] = [1, 2, 3.7, 6.7, 12.3, 22.7];
/**
 * 상점 기본 가격(마을1 기준 골드). 물약은 크기가 곧 단계라 배율을 곱하지 않는다.
 * 비약은 연금술사 제작비(마을④부터), 보호 주문서는 골드 상점 주 2장 한정(PRD 4.3).
 */
export const SHOP_PRICE = {
  potion: { s: 20, m: 70, l: 180 },
  elixir: 400,
  scrollReturn: 40,
  featherRevive: 250,
  protect: 4000,
  /** 옵션 재련 1회(마을③) */
  reroll: 600,
  /** 보석 탈착 1회(마을③) */
  unsocket: 300,
  /**
   * 마을 장비 상점(S7, STAGE-WORLD-PRD 5.2) 부위별 기본 가격(마을1 기준, 실제 = × 상점 단계 배율). 마을②~④에서만 판다.
   * 가안(골드 소비처, 선택 구매): 마을② 무기 3,000 = 지역2 노말 클리어 약 3판 수입. 드롭보다 약한 품목이라 주간 수입 시뮬(tools/sim)에는 넣지 않는다
   */
  gear: { weapon: 1500, helm: 800, armor: 1200, gloves: 800, boots: 800, amulet: 1000 },
} as const;

// ── 엔드게임 싱크(3개월 루프 C4, PROGRESSION-720H 6절) ──────────
// +15 뒤 골드·최상 강화석 소비처. 초월 강화·숙련 재분배는 shared/mastery.ts(게임 구현됨). 아래 두 개는 데이터 계약(설계, 게임 구현은 다음 덩어리):
// 전설 각인은 장비 개체 필드, 보석 6·7등급은 보석 코드를 늘려야 해서 저장 형식이 바뀐다 — tools/sim/sinks720.ts가 같은 값으로 90일 골드를 잰다.
/**
 * 전설 각인(설계): 전설 장비의 고유 효과를 1→10단계로 올린다(단계당 고유 효과 +10%). 확정 성공, 최상 강화석 + 골드.
 * 1차 환생 뒤(대장간). 장착 전설 하나당 강화석 5,150·골드 5,150만. 단계를 잘게 나눠 하루 수입(약 850만)으로 거의 매일 한 단계를 올린다.
 */
export const ENGRAVE_STEPS: readonly { readonly stones: number; readonly gold: number }[] = [
  { stones: 150, gold: 1_500_000 }, { stones: 200, gold: 2_000_000 }, { stones: 250, gold: 2_500_000 }, { stones: 300, gold: 3_000_000 },
  { stones: 400, gold: 4_000_000 }, { stones: 500, gold: 5_000_000 }, { stones: 600, gold: 6_000_000 }, { stones: 750, gold: 7_500_000 },
  { stones: 900, gold: 9_000_000 }, { stones: 1100, gold: 11_000_000 },
];
/** 전설 각인 단계당 고유 효과 배율 증가 */
export const ENGRAVE_EFFECT_PER_STEP = 0.1;
/**
 * 보석 6·7등급(설계): 5등급(완벽한) 3개 → 6등급(찬란한) 1개, 6등급 3개 → 7등급(태초의) 1개. 확정, 수수료만.
 * 6등급은 1차 환생, 7등급은 2차 환생 뒤 보석상. 새 등급은 기존 보석 코드(0..14)를 바꾸지 않고 뒤에 덧붙인다(저장 호환).
 */
export const GEM_HIGH = { names: ['찬란한', '태초의'] as readonly string[], fee: [400_000, 1_500_000] as readonly number[], gateCycle: [1, 2] as readonly number[] } as const;

// ── C4 대장간 심화: 재련(큐브)·각인·천공 (docs/prd/c4-forge-deep.md 4장, 계약 src/shared/forge/contract.ts) ──
/** 재련·천공을 여는 마을(대장간 재련 탭은 마을③부터) */
export const FORGE_TOWN = 3;
/** 재련 가루 소비 아이템 id(cons, STACK_CAP까지, 귀속 — 젬으로 팔지 않는다, PRD 7장) */
export const REROLL_DUST_ID = 'reroll_dust';
/** 재련 골드 계수. PRD 4.2 가안 0.2 → 0.5(tools/sim/forge720.ts 실측: 일 가루 약 83개라 0.2면 재련 골드가 총수입 3.6%로 목표 8~10% 미달, 0.5 = 8.9%, 각인 지연 ≤5일·끝 골드 ≥0 통과. 대안 = 가루 공급 ×2.4, docs/loop/3month-highend/sim-forge.md) */
export const REROLL_GOLD_K = 0.5;
/** 잠금 줄 수(0·1·2)별 골드 배율과 가루 수 */
export const REROLL_LOCK_MULT: readonly number[] = [1, 1.6, 2.6];
export const REROLL_DUST: readonly number[] = [3, 5, 8];
/** 한 장비에서 잠글 수 있는 줄 수 상한 */
export const REROLL_LOCK_MAX = 2;
/** 확정형(지정 옵션 1줄 보장) 배율: 골드 ×2·가루 ×3 */
export const REROLL_SURE = { gold: 2, dust: 3 } as const;
/** 등급별 분해 재련 가루(영웅 3·세트 4·전설 12, 아이템 레벨 가산 없음). 희귀 이하 0 */
export const DUST_FROM_SALVAGE = { common: 0, rare: 0, epic: 3, set: 4, legend: 12 } as const;
/** 재련 카운터 상한(서버 상한 검사 — 정상 플레이로 닿을 수 없는 값). 하루 수백 회 × 수년보다 넉넉하다 */
export const REROLL_COUNTER_MAX = 10_000_000;
/** 각인 최대 단계(ENGRAVE_STEPS 길이)와 해금 환생 회차 */
export const ENGRAVE_MAX = 10;
export const ENGRAVE_GATE_CYCLE = 1;
/** 전설 소켓 천공(3→4칸, 장비당 1회, 확정): 골드 + 최상 강화석, 1차 환생 뒤 */
export const DRILL_COST = { gold: 5_000_000, stones: 100, gateCycle: 1 } as const;
/** 천공 포함 한 장비 소켓 상한 */
export const SOCKET_MAX_DRILLED = 4;
